import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { Button, ButtonType } from '@/components/atoms/button'
import { InputField } from '@/components/molecules/input-field'
import { AddFormFieldButton } from '@/components/molecules/add-form-field-button'
import { EditFooter } from '@/components/organisms/edit-footer'
import { DeleteIcon } from '@/components/molecules/settings/icon/delete-icon'
import { usePushNotificationContext } from '@/contexts/push-notification'
import { AccountContext } from '@/contexts/account'
import { isPushSupported } from '@/hooks/use-push-notification'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { fingerprint } from '@/utils/fingerprint'
import { fetchPushSubscriptionCtiLineSettings, updatePushSubscriptionCtiLineSettings } from '@/apis/aipass'

type FormValue = {
  settings: {
    lineInfo: string
  }[]
}

const createEmptySetting = () => ({ lineInfo: '' })

export const SettingNotification: React.FC = () => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { permission, isSubscribed, isInitializing, subscribe, unsubscribe } = usePushNotificationContext()
  const { employeeStaff } = useContext(AccountContext)
  const [loading, setLoading] = useState(false)
  const [initialSettings, setInitialSettings] = useState<FormValue['settings']>([createEmptySetting()])
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValue>({
    defaultValues: {
      settings: [createEmptySetting()],
    },
  })
  const lineSettingInput = useFieldArray({
    control,
    name: 'settings',
  })

  const currentStaffId = useMemo(() => {
    const currentStaff = employeeStaff.find(staff => staff.isCurrent)
    if (currentStaff?.id == null || currentStaff.id === '') {
      return undefined
    }

    const parsedStaffId = Number(currentStaff.id)
    return Number.isNaN(parsedStaffId) ? undefined : parsedStaffId
  }, [employeeStaff])

  const initializeComponent = async () => {
    try {
      setLoading(true)
      const result = await fetchPushSubscriptionCtiLineSettings({ fingerprint })
      const nextSettings = result.settings.length > 0 ? result.settings : [createEmptySetting()]
      setInitialSettings(nextSettings)
      reset({ settings: nextSettings })
    } catch (error) {
      errorHandler(error)
    } finally {
      setLoading(false)
    }
  }

  const doSave = handleSubmit(async ({ settings }) => {
    try {
      setLoading(true)
      const nextSettings = settings.map(setting => ({ lineInfo: setting.lineInfo.trim() }))
      await updatePushSubscriptionCtiLineSettings({ fingerprint, settings: nextSettings })
      setInitialSettings(nextSettings.length > 0 ? nextSettings : [createEmptySetting()])
      reset({ settings: nextSettings.length > 0 ? nextSettings : [createEmptySetting()] })
    } catch (error) {
      errorHandler(error)
    } finally {
      setLoading(false)
    }
  })

  const doCancel = () => {
    reset({ settings: initialSettings })
  }

  /** SW 登録や権限要求の失敗は Promise の reject で返るため、unhandled rejection にせず利用者へ通知する */
  const withErrorHandling = (action: () => Promise<void>) => async () => {
    try {
      await action()
    } catch (error) {
      errorHandler(error)
    }
  }

  useEffect(() => {
    initializeComponent()
  }, [currentStaffId])

  const footerContent = isDirty ? <EditFooter onSave={doSave} onCancel={doCancel} isDisabledSave={loading} /> : undefined

  return (
    <SettingsLayout loading={isInitializing || loading} footerContent={footerContent}>
      <div css={containerStyle}>
        <div css={sectionHeaderStyle}>{t('Push notification')}</div>
        <div css={sectionBodyStyle}>
          {!isPushSupported() ? (
            <p css={descriptionStyle}>{t('This browser does not support push notifications.')}</p>
          ) : permission === 'denied' ? (
            <p css={descriptionStyle}>{t('Push notifications are blocked. Please allow notifications in your browser settings.')}</p>
          ) : (
            <>
              <p css={descriptionStyle}>{t('Enable push notifications to receive alerts for check-ins, cleaning, messages, and more.')}</p>
              <div css={buttonRowStyle}>
                {isSubscribed ? (
                  <Button buttonType={ButtonType.Secondary} width={160} height={36} onClick={withErrorHandling(unsubscribe)}>
                    {t('Disable notifications')}
                  </Button>
                ) : (
                  <Button buttonType={ButtonType.Primary} width={160} height={36} onClick={withErrorHandling(subscribe)}>
                    {t('Enable notifications')}
                  </Button>
                )}
              </div>
              <div css={statusStyle}>
                <span css={statusLabelStyle}>{t('Status')}：</span>
                <span css={isSubscribed ? statusEnabledStyle : statusDisabledStyle}>
                  {isSubscribed ? t('Notifications enabled') : t('Notifications disabled')}
                </span>
              </div>
            </>
          )}
        </div>

        <div css={sectionHeaderStyle}>{t('CTI line settings')}</div>
        <div css={sectionBodyStyle} style={{ width: 420 }}>
          {lineSettingInput.fields.map((field, index) => (
            <div key={field.id} css={fieldStyle}>
              <div css={fieldRowStyle}>
                <div css={fieldLabelStyle}>{t('Line number')}</div>
                <Controller
                  control={control}
                  name={`settings.${index}.lineInfo`}
                  rules={{
                    required: t('Required field has not been entered'),
                    pattern: { value: /^\d+$/, message: t('Please enter numbers only, without symbols or spaces') },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <InputField
                      type="text"
                      value={value}
                      handleChangeData={onChange}
                      placeholder="05012345678"
                      marginBottom={0}
                      width="240px"
                      fieldWidth="unset"
                      error={errors.settings?.[index]?.lineInfo ? ' ' : ''}
                    />
                  )}
                />
                <DeleteIcon onClick={() => lineSettingInput.remove(index)} />
              </div>
              {errors.settings?.[index]?.lineInfo?.message && (
                <div css={errorMessageStyle}>{errors.settings[index]?.lineInfo?.message}</div>
              )}
            </div>
          ))}

          <AddFormFieldButton onAdd={() => lineSettingInput.append(createEmptySetting())} label={t('Add line')} />
        </div>
      </div>
    </SettingsLayout>
  )
}

const containerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
})

const sectionHeaderStyle = css({
  fontSize: 14,
  fontWeight: 'bold',
  borderBottom: '1px solid #F2F2F2',
  color: '#272727',
})

const sectionBodyStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
})

const descriptionStyle = css({
  fontSize: 13,
  color: '#676767',
  lineHeight: '1.6',
})

const buttonRowStyle = css({
  display: 'flex',
  gap: 12,
})

const fieldStyle = css({
  border: '1px solid #CCCCCC',
  borderRadius: 10,
  padding: '12px 24px',
})

const fieldRowStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
})

const fieldLabelStyle = css({
  width: 96,
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: 1.2,
  color: '#676767',
})

const errorMessageStyle = css({
  fontSize: 10,
  color: 'rgba(255,0,0,0.5)',
  marginTop: 8,
  marginLeft: 112,
})

const statusStyle = css({
  display: 'flex',
  alignItems: 'center',
  fontSize: 13,
})

const statusLabelStyle = css({
  color: '#676767',
})

const statusEnabledStyle = css({
  color: '#62BF03',
  fontWeight: 'bold',
})

const statusDisabledStyle = css({
  color: '#AAAAAA',
})
